import React, { useState, useEffect, useCallback } from "react";
import { toast } from "react-toastify";
import {
  MenuItem, Box, IconButton,
  Table, TableHead, TableBody, TableRow, TableCell, TableContainer,
} from "@mui/material";
import { C } from "../constants";
import { IPD_COLORS } from "../../ipd/utils/ipdColors";
import IpdTextField from "../../ipd/utils/IpdTextField";
import IpdDateField from "../../ipd/utils/IpdDateField";
import IpdFormField from "../../ipd/utils/IpdFormField";
import IpdButton from "../../ipd/utils/IpdButton";
import IpdSectionCard from "../../ipd/utils/IpdSectionCard";
import IpdDataGrid from "../../ipd/utils/IpdDataGrid";
import SvgIcon from "../SvgIcon";
import {
  listSchemePayers,
  listSchemeMappings,
  createSchemeMapping,
  updateSchemeMapping,
  deleteSchemeMapping,
} from "../scheme-api";
import {
  listServiceGroups,
  listServiceClassifications,
  listMedicalServices,
  toBillingError,
} from "../billing-api";
import { listLookups } from "../service-master-api";

const todayISO = () => new Date().toISOString().slice(0, 10);

function fmtDate(d) {
  if (!d) return "—";
  const dt = new Date(d);
  if (Number.isNaN(dt.getTime())) return String(d);
  return dt.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
}

// ── Hospital service picker ───────────────────────────────────────────────────
// Reuses the exact rate-card cascade: service group → classification (+ bed type,
// sample, name filter) → GET service-catalog/services/. Picking a service sets
// hospital_service_code (+ hospital_service_name) for the mapping being built.
function ServicePicker({ pickedCode, onPick }) {
  const [groups, setGroups] = useState([]);
  const [groupId, setGroupId] = useState("");
  const [classifications, setClassifications] = useState([]);
  const [classificationId, setClassificationId] = useState("");
  const [bedTypes, setBedTypes] = useState([]);
  const [bedTypeCode, setBedTypeCode] = useState("");
  const [sampleTypes, setSampleTypes] = useState([]);
  const [sampleId, setSampleId] = useState("");
  const [services, setServices] = useState([]);
  const [servicesLoading, setServicesLoading] = useState(false);
  const [nameFilter, setNameFilter] = useState("");

  useEffect(() => {
    (async () => {
      try { setGroups(await listServiceGroups()); } catch { /* non-fatal */ }
    })();
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const { bedTypes: bt, sampleTypes: st } = await listLookups({ activeOnly: true });
        setBedTypes(bt);
        setSampleTypes(st);
      } catch { /* non-fatal — bed/sample filters just stay empty */ }
    })();
  }, []);

  // Classifications for the selected group.
  useEffect(() => {
    setClassificationId("");
    setClassifications([]);
    if (!groupId) return;
    let cancelled = false;
    (async () => {
      try {
        const cls = await listServiceClassifications(groupId);
        if (!cancelled) setClassifications(cls);
      } catch { if (!cancelled) setClassifications([]); }
    })();
    return () => { cancelled = true; };
  }, [groupId]);

  // Services for the group (+ classification, bed type, sample when chosen).
  useEffect(() => {
    if (!groupId) { setServices([]); return; }
    let cancelled = false;
    setServicesLoading(true);
    (async () => {
      try {
        const svcs = await listMedicalServices({
          groupId,
          classificationId: classificationId || undefined,
          bedTypeCode: bedTypeCode || undefined,
          sampleId: sampleId || undefined,
        });
        if (!cancelled) setServices(svcs);
      } catch {
        if (!cancelled) setServices([]);
      } finally {
        if (!cancelled) setServicesLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [groupId, classificationId, bedTypeCode, sampleId]);

  const nf = nameFilter.trim().toLowerCase();
  const visibleServices = nf
    ? services.filter((s) => [s.name, s.code].filter(Boolean).join(" ").toLowerCase().includes(nf))
    : services;

  // No bed type chosen → the alias applies to ALL bed types: key it on the shared
  // unique_code and show one row per service (deduped). A bed type chosen → alias that
  // exact bed variant (its own service_code).
  const allBeds = !bedTypeCode;
  const rows = allBeds
    ? Object.values(
        visibleServices.reduce((acc, s) => {
          const key = s.uniqueCode || s.code;
          if (key && !acc[key]) acc[key] = s;
          return acc;
        }, {})
      )
    : visibleServices;

  return (
    <Box>
      <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: "12px", marginBottom: "14px" }}>
        <IpdFormField label="Service group" required>
          <IpdTextField select fullWidth value={groupId} onChange={(e) => setGroupId(e.target.value)}>
            <MenuItem value="">Select group…</MenuItem>
            {groups.map((g) => <MenuItem key={g.id} value={g.id}>{g.name}</MenuItem>)}
          </IpdTextField>
        </IpdFormField>
        <IpdFormField label="Classification">
          <IpdTextField select fullWidth value={classificationId} onChange={(e) => setClassificationId(e.target.value)} disabled={!groupId}>
            <MenuItem value="">{!groupId ? "Select group first" : "All classifications"}</MenuItem>
            {classifications.map((c) => <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>)}
          </IpdTextField>
        </IpdFormField>
        <IpdFormField label="Bed type">
          <IpdTextField select fullWidth value={bedTypeCode} onChange={(e) => setBedTypeCode(e.target.value)} disabled={!groupId}>
            <MenuItem value="">All bed types</MenuItem>
            {bedTypes.map((b) => <MenuItem key={b.id} value={b.code}>{b.name} ({b.code})</MenuItem>)}
          </IpdTextField>
        </IpdFormField>
        <IpdFormField label="Sample type (labs)">
          <IpdTextField select fullWidth value={sampleId} onChange={(e) => setSampleId(e.target.value)} disabled={!groupId}>
            <MenuItem value="">All samples</MenuItem>
            {sampleTypes.map((s) => <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>)}
          </IpdTextField>
        </IpdFormField>
        <IpdFormField label="Filter by name">
          <IpdTextField fullWidth value={nameFilter} onChange={(e) => setNameFilter(e.target.value)} placeholder="chest, echo…" disabled={!groupId} />
        </IpdFormField>
      </Box>

      {!groupId ? (
        <Box sx={{ padding: "16px", backgroundColor: IPD_COLORS.pageBg, border: `1px solid ${C.border}`, borderRadius: "10px", fontSize: "13px", color: C.textSub }}>
          Pick a service group to list its services, then choose the one to alias.
        </Box>
      ) : (
        <TableContainer sx={{ border: `1px solid ${C.border}`, borderRadius: "10px", maxHeight: "360px" }}>
          <Table stickyHeader sx={{ minWidth: "640px" }}>
            <TableHead>
              <TableRow>
                {["Unique code", "Service", "Bed", ""].map((h, i) => (
                  <TableCell key={i} sx={{ padding: "10px 14px", fontSize: "11px", fontWeight: 700, color: C.textSub, textTransform: "uppercase", whiteSpace: "nowrap", backgroundColor: IPD_COLORS.pageBg }}>{h}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {servicesLoading ? (
                <TableRow><TableCell colSpan={4} align="center" sx={{ padding: "22px", color: C.textSub, fontSize: "13px" }}>Loading services…</TableCell></TableRow>
              ) : rows.length === 0 ? (
                <TableRow><TableCell colSpan={4} align="center" sx={{ padding: "22px", color: C.textSub, fontSize: "13px" }}>No services in this scope.</TableCell></TableRow>
              ) : (
                rows.map((s) => {
                  // All-beds alias keys on unique_code; bed-specific keys on service_code.
                  const codeToUse = allBeds ? (s.uniqueCode || s.code) : s.code;
                  const selected = pickedCode && codeToUse === pickedCode;
                  return (
                    <TableRow key={s.id} sx={{ backgroundColor: selected ? IPD_COLORS.info.bg : "transparent" }}>
                      <TableCell sx={{ padding: "8px 14px", fontSize: "13px", fontWeight: 700, color: C.primary, whiteSpace: "nowrap" }}>{s.uniqueCode || "—"}</TableCell>
                      <TableCell sx={{ padding: "8px 14px", fontSize: "13px", color: C.text }}>
                        {s.name}
                        <Box sx={{ fontSize: "11px", color: C.textSub, fontFamily: "monospace" }}>{allBeds ? (s.uniqueCode || s.code || "—") : (s.code || "—")}</Box>
                      </TableCell>
                      <TableCell sx={{ padding: "8px 14px", fontSize: "12px", color: C.textSub, whiteSpace: "nowrap" }}>{allBeds ? "All beds" : (s.bedTypeCode || "—")}</TableCell>
                      <TableCell sx={{ padding: "8px 14px", whiteSpace: "nowrap" }}>
                        <IpdButton
                          size="small"
                          variant={selected ? "contained" : "outlined"}
                          onClick={() => onPick({ code: codeToUse, name: s.name })}
                          disabled={!codeToUse}
                        >
                          {selected ? "Selected" : "Select"}
                        </IpdButton>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
}

// ── Mapping editor (service picker + alias inputs → POST/PUT) ──────────────────
// priority is intentionally not surfaced in the UI — it stays fixed at 100 and is
// sent on every create/update so the backend's tie-break field keeps a stable value.
const DEFAULT_PRIORITY = 100;

const emptyAlias = () => ({
  scheme_service_code: "",
  scheme_service_name: "",
  effective_from: todayISO(),
  effective_to: "",
  remarks: "",
});

function MappingEditor({ schemeValue, editing, onSaved, onCancelEdit }) {
  const [picked, setPicked] = useState(null); // { code, name }
  const [alias, setAlias] = useState(emptyAlias);
  const [saving, setSaving] = useState(false);

  // Load the row being edited into the form (hospital service can't be re-picked
  // on edit — the mapping key stays fixed; change dates/name/priority instead).
  useEffect(() => {
    if (editing) {
      setPicked({ code: editing.hospitalServiceCode, name: editing.hospitalServiceName });
      setAlias({
        scheme_service_code: editing.schemeServiceCode || "",
        scheme_service_name: editing.schemeServiceName || "",
        effective_from: editing.effectiveFrom || todayISO(),
        effective_to: editing.effectiveTo || "",
        remarks: editing.remarks || "",
      });
    } else {
      setPicked(null);
      setAlias(emptyAlias());
    }
  }, [editing]);

  const set = (k) => (e) => setAlias((a) => ({ ...a, [k]: e.target.value }));
  const setDate = (k) => (iso) => setAlias((a) => ({ ...a, [k]: iso }));

  const save = async () => {
    if (!picked?.code) { toast.error("Pick a hospital service to alias"); return; }
    if (!alias.scheme_service_name.trim()) { toast.error("Scheme service name is required"); return; }
    if (!alias.effective_from) { toast.error("Effective from is required"); return; }
    if (alias.effective_to && alias.effective_to < alias.effective_from) {
      toast.error("Effective to must be on or after effective from");
      return;
    }
    setSaving(true);
    const body = {
      // The EMR resolves the scheme to the billing payer an admission on this scheme
      // lands on (creating it on first use), so the alias always matches the patient.
      scheme_value: schemeValue,
      hospital_service_code: picked.code,
      scheme_service_name: alias.scheme_service_name.trim(),
      effective_from: alias.effective_from,
      priority: DEFAULT_PRIORITY,
      is_active: true,
    };
    if (picked.name) body.hospital_service_name = picked.name;
    if (alias.scheme_service_code.trim()) body.scheme_service_code = alias.scheme_service_code.trim();
    if (alias.effective_to) body.effective_to = alias.effective_to;
    if (alias.remarks.trim()) body.remarks = alias.remarks.trim();
    try {
      if (editing) await updateSchemeMapping(editing.id, body);
      else await createSchemeMapping(body);
      toast.success(editing ? "Mapping updated" : "Mapping saved");
      setPicked(null);
      setAlias(emptyAlias());
      onSaved();
    } catch (err) {
      if (err.status === 409) {
        toast.error("A mapping for this service already exists in that date range — edit it or change the dates/priority.");
      } else if (err.status === 400) {
        toast.error(toBillingError(err, "That payer is not a scheme"));
      } else {
        toast.error(toBillingError(err, "Could not save mapping"));
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box>
      {editing ? (
        <Box sx={{ padding: "12px 16px", backgroundColor: IPD_COLORS.info.bg, border: `1px solid ${IPD_COLORS.info.border}`, borderRadius: "10px", marginBottom: "14px", display: "flex", justifyContent: "space-between", alignItems: "center", gap: "12px", flexWrap: "wrap" }}>
          <Box sx={{ fontSize: "13px", color: C.text }}>
            Editing alias for <b>{editing.hospitalServiceName || editing.hospitalServiceCode}</b>
            <Box component="span" sx={{ color: C.textSub, fontFamily: "monospace", marginLeft: "6px" }}>{editing.hospitalServiceCode}</Box>
          </Box>
          <IpdButton variant="outlined" size="small" onClick={onCancelEdit}>Cancel edit</IpdButton>
        </Box>
      ) : (
        <ServicePicker pickedCode={picked?.code} onPick={setPicked} />
      )}

      {picked && (
        <Box sx={{ marginTop: "14px", padding: "14px 16px", backgroundColor: IPD_COLORS.pageBg, border: `1px solid ${C.border}`, borderRadius: "10px" }}>
          <Box sx={{ fontSize: "12px", fontWeight: 700, color: C.text, marginBottom: "10px" }}>
            Invoice alias for <Box component="span" sx={{ color: C.primary }}>{picked.name || picked.code}</Box>
            <Box component="span" sx={{ fontWeight: 500, color: C.textSub, fontFamily: "monospace", marginLeft: "6px" }}>{picked.code}</Box>
          </Box>
          <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: "12px" }}>
            <IpdFormField label="Scheme service code">
              <IpdTextField fullWidth value={alias.scheme_service_code} onChange={set("scheme_service_code")} placeholder="WBHS-RAD-001" />
            </IpdFormField>
            <IpdFormField label="Scheme service name" required>
              <IpdTextField fullWidth value={alias.scheme_service_name} onChange={set("scheme_service_name")} placeholder="X-Ray (Chest)" />
            </IpdFormField>
            <IpdFormField label="Effective from" required>
              <IpdDateField value={alias.effective_from} onChange={setDate("effective_from")} />
            </IpdFormField>
            <IpdFormField label="Effective to (optional)">
              <IpdDateField value={alias.effective_to} onChange={setDate("effective_to")} minDate={alias.effective_from ? new Date(alias.effective_from) : undefined} />
            </IpdFormField>
          </Box>
          <Box sx={{ marginTop: "12px", display: "flex", gap: "10px", alignItems: "center", flexWrap: "wrap" }}>
            <IpdButton onClick={save} loading={saving}>
              {editing ? "Update mapping" : "Save mapping"}
            </IpdButton>
            <Box component="span" sx={{ fontSize: "11px", color: C.textSub }}>
              The scheme name (and code, if given) prints on this scheme's invoices. Existing invoices are never re-priced.
            </Box>
          </Box>
        </Box>
      )}
    </Box>
  );
}

// ── Saved mappings table ──────────────────────────────────────────────────────
function SavedMappings({ mappings, loading, onEdit, onDeactivate, busyId }) {
  const columns = [
    { field: "hospitalServiceCode", headerName: "Hospital code", flex: 1, minWidth: 130, sortable: false,
      renderCell: (p) => <Box component="span" sx={{ fontSize: "13px", fontWeight: 700, color: C.text, whiteSpace: "nowrap", fontFamily: "monospace" }}>{p.value || "—"}</Box> },
    { field: "hospitalServiceName", headerName: "Hospital name", flex: 1.3, minWidth: 150, sortable: false,
      renderCell: (p) => <Box component="span" sx={{ fontSize: "13px", color: C.textSub }}>{p.value || "—"}</Box> },
    { field: "schemeServiceCode", headerName: "Scheme code", flex: 1, minWidth: 120, sortable: false,
      renderCell: (p) => <Box component="span" sx={{ fontSize: "13px", color: C.text, whiteSpace: "nowrap", fontFamily: "monospace" }}>{p.value || "—"}</Box> },
    { field: "schemeServiceName", headerName: "Scheme name (printed)", flex: 1.4, minWidth: 160, sortable: false,
      renderCell: (p) => <Box component="span" sx={{ fontSize: "13px", fontWeight: 600, color: C.text }}>{p.value || "—"}</Box> },
    { field: "effectiveFrom", headerName: "Effective from", flex: 1, minWidth: 120, sortable: false,
      renderCell: (p) => <Box component="span" sx={{ fontSize: "13px", color: C.textSub, whiteSpace: "nowrap" }}>{fmtDate(p.value)}</Box> },
    { field: "effectiveTo", headerName: "Effective to", flex: 1, minWidth: 120, sortable: false,
      renderCell: (p) => <Box component="span" sx={{ fontSize: "13px", color: C.textSub, whiteSpace: "nowrap" }}>{fmtDate(p.value)}</Box> },
    { field: "active", headerName: "Active", width: 90, sortable: false,
      renderCell: (p) => (
        <Box component="span" sx={{ fontSize: "12px", fontWeight: 600, whiteSpace: "nowrap", color: p.value ? IPD_COLORS.success.text : IPD_COLORS.textSecondary }}>
          {p.value ? "Active" : "Inactive"}
        </Box>
      ) },
    { field: "actions", headerName: "", width: 110, sortable: false, align: "right", headerAlign: "right",
      renderCell: (p) => (
        <Box sx={{ whiteSpace: "nowrap" }}>
          <IconButton size="small" onClick={() => onEdit(p.row)} title="Edit">
            <SvgIcon name="edit" size={16} color={C.text} />
          </IconButton>
          <IconButton size="small" onClick={() => onDeactivate(p.row)} disabled={busyId === p.row.id} title="Deactivate">
            <SvgIcon name="trash" size={16} color={C.danger} />
          </IconButton>
        </Box>
      ) },
  ];

  return (
    <IpdDataGrid
      embedded
      hideFooter
      disableColumnMenu
      loading={loading}
      rows={mappings}
      columns={columns}
      emptyMessage="No invoice names mapped yet — pick a service above and save its scheme name."
    />
  );
}

// ── Screen root ───────────────────────────────────────────────────────────────
export default function SchemeServiceNames() {
  const [schemes, setSchemes] = useState([]);
  const [schemeValue, setSchemeValue] = useState("");
  const [mappings, setMappings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const loadSchemes = useCallback(async () => {
    try { setSchemes(await listSchemePayers()); } catch { /* non-fatal */ }
  }, []);

  const loadMappings = useCallback(async () => {
    if (!schemeValue) { setMappings([]); return; }
    setLoading(true);
    try {
      setMappings(await listSchemeMappings({ schemeValue, isActive: true }));
    } catch (err) {
      toast.error(toBillingError(err, "Could not load mappings"));
      setMappings([]);
    } finally {
      setLoading(false);
    }
  }, [schemeValue]);

  useEffect(() => { loadSchemes(); }, [loadSchemes]);
  useEffect(() => { setEditing(null); loadMappings(); }, [loadMappings]);

  const deactivate = async (row) => {
    setBusyId(row.id);
    try {
      await deleteSchemeMapping(row.id);
      toast.success(`Alias for ${row.hospitalServiceCode || "service"} removed`);
      if (editing?.id === row.id) setEditing(null);
      await loadMappings();
    } catch (err) {
      toast.error(toBillingError(err, "Could not remove mapping"));
    } finally {
      setBusyId(null);
    }
  };

  const selectedScheme = schemes.find((s) => s.schemeValue === schemeValue);

  return (
    <Box sx={{ animation: "ipdFadeIn 0.2s ease" }}>
      {/* Scheme band — the admission form's own scheme list; nothing is created here */}
      <IpdSectionCard
        title="Scheme"
        subtitle="Pick the scheme whose invoice service names you want to manage"
      >
        <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: "16px" }}>
          <IpdFormField label="Scheme" required>
            <IpdTextField select fullWidth value={schemeValue} onChange={(e) => setSchemeValue(e.target.value)} SelectProps={{ displayEmpty: true }}>
              <MenuItem value="">Select scheme…</MenuItem>
              {schemes.map((s) => (
                <MenuItem key={s.schemeValue} value={s.schemeValue}>{s.label}</MenuItem>
              ))}
            </IpdTextField>
          </IpdFormField>
          {selectedScheme && (
            <Box sx={{ fontSize: "12px", color: C.textSub, alignSelf: "center" }}>
              Same scheme list as the admission screen's Payer dropdown
            </Box>
          )}
        </Box>
      </IpdSectionCard>

      {!schemeValue ? (
        <Box sx={{ padding: "16px 20px", backgroundColor: IPD_COLORS.info.bg, border: `1px solid ${C.border}`, borderRadius: "10px", fontSize: "13px", color: C.textSub }}>
          Pick a scheme to manage its invoice names.
        </Box>
      ) : (
        <>
          <IpdSectionCard
            title="Service name mapping"
            subtitle="Map a hospital service to the name & code this scheme prints on its invoices"
          >
            <MappingEditor
              key={editing?.id || "new"}
              schemeValue={schemeValue}
              editing={editing}
              onSaved={() => { setEditing(null); loadMappings(); }}
              onCancelEdit={() => setEditing(null)}
            />
          </IpdSectionCard>

          <IpdSectionCard title={`Saved mappings (${mappings.length})`} subtitle="Active invoice-name aliases for this scheme">
            <SavedMappings
              mappings={mappings}
              loading={loading}
              onEdit={setEditing}
              onDeactivate={deactivate}
              busyId={busyId}
            />
          </IpdSectionCard>
        </>
      )}
    </Box>
  );
}
